<template>
  <div class="new-filter">
    <template v-if="filteredFilterSection.values?.length">
      <Collapsible
        class="filter__section"
        :isCollapsed="isCollapsed"
        @toggle="onToggle"
        :isCollapsible="true"
        :max-height="28"
        :min-height-to-collapse="
          filteredFilterSection.alwaysExpanded ? 10000 : 65
        "
      >
        <template #header>
          <div class="filter__section-header filter__section-header--parent">
            <h3>
              {{ filteredFilterSection.title }}
            </h3>
            <UpDownArrow
              class="filter__section-icon"
              :type="isCollapsed ? 'down' : 'up'"
            />
          </div>
        </template>
        <template #body>
          <FilterSectionComponent
            v-for="filterSection of (filteredFilterSection.values as any[])"
            :key="filterSection.attribute"
            :isCollapsible="false"
            :filter="filterSection"
            :selected-filter="
              selectedFilters[
                `${filteredFilterSection.attribute}_${filterSection.attribute}`
              ]
            "
            @toggle-filter="
              toggleFilter(
                `${filteredFilterSection.attribute}_${filterSection.attribute}`,
                $event
              )
            "
            @toggle-parent-filter="
              toggleFilter(`${filteredFilterSection.attribute}_parents`, $event)
            "
            :parentFilter="filteredFilterSection.attribute"
            :parentSelected="
              selectedFilters[`${filteredFilterSection.attribute}_parents`]
            "
          >
          </FilterSectionComponent>

          <div
            class="mt-md"
            v-if="filteredFilterSection.attribute == THEMES_ATTRIBUTE"
          >
            <sib-checkbox
              class="topic-checkbox"
              name="non-topic"
              label="Non topic"
              v-model="topic"
              @change="toggleFilter(NON_TOPIC_ATTRIBUTE, topic.toString())"
            ></sib-checkbox>
          </div>
        </template>
      </Collapsible>
    </template>

    <template v-else>
      <FilterSectionComponent
        :filter="filteredFilterSection"
        :selected-filter="selectedFilters[filteredFilterSection.attribute]"
        @toggle-filter="toggleFilter(filteredFilterSection.attribute, $event)"
      >
      </FilterSectionComponent>
    </template>
  </div>
</template>

<script setup lang="ts">
import { FilterSection } from '@/types'
import Collapsible from '@/ui-kits/Collapsible.vue'
import FilterSectionComponent from './CheckBoxFilterSection.vue'
import UpDownArrow from '@/ui-kits/UpDownArrow.vue'
import { onMounted, onUnmounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import {
  EVENT_OPEN_FILTER_POPUP_BASE_ON_ATTRIBUTE,
  SEARCH_RESTRICT_BY_QUERY_STRING_PREFIX,
} from '@/utils'
import EventBus from '@/services/eventbus'

const route = useRoute()

interface SelectedFilters {
  [key: string]: string[]
}

const props = withDefaults(
  defineProps<{
    toggleFilter: (value: string, event: string) => void
    selectedFilters: SelectedFilters
    filteredFilterSection: FilterSection
  }>(),
  {}
)

const isCollapsed = ref(true)
const NON_TOPIC_ATTRIBUTE = 'non_topic'
const THEMES_ATTRIBUTE = 'themes'
const topic = ref(
  route.query[
    `${SEARCH_RESTRICT_BY_QUERY_STRING_PREFIX}${NON_TOPIC_ATTRIBUTE}`
  ] == 'true'
)
const expandCollapseQueryPrefix = 'show_filter'
const queryExpandCollapseFilter: any = ref([])

onMounted(() => {
  expandCollapseFilter()
  if (props.filteredFilterSection.values?.length)
    EventBus.on(EVENT_OPEN_FILTER_POPUP_BASE_ON_ATTRIBUTE, expandCollapseFilter)
})

onUnmounted(() => {
  if (props.filteredFilterSection.values?.length)
    EventBus.off(
      EVENT_OPEN_FILTER_POPUP_BASE_ON_ATTRIBUTE,
      expandCollapseFilter
    )
})

function expandCollapseFilter() {
  // expand collapse filter
  for (const key in route.query) {
    if (!key.startsWith(`${expandCollapseQueryPrefix}_`)) continue
    if (Object.prototype.hasOwnProperty.call(route.query, key)) {
      const value = route.query[key]
      let keyWithoutPrefix = key
        ?.trim()
        ?.toLowerCase()
        ?.split(`${expandCollapseQueryPrefix}_`)
        .pop()
      keyWithoutPrefix?.replaceAll('_', '')
      keyWithoutPrefix?.replaceAll('-', '')
      if (value) queryExpandCollapseFilter.value.push(keyWithoutPrefix)
    }
  }

  let attributeFilter = props.filteredFilterSection?.attribute
    ?.trim()
    ?.toLowerCase()
  attributeFilter?.replaceAll('_', '')
  attributeFilter?.replaceAll('-', '')
  const isExpandCollapseFilter =
    queryExpandCollapseFilter.value?.includes(attributeFilter)
  if (isExpandCollapseFilter) onToggle(false)
}

function onToggle(value: boolean) {
  isCollapsed.value = value
}
</script>
<style lang="scss">
.new-filter {
  .filter__section-header--parent {
    justify-content: start;
  }
  .topic-checkbox {
    width: 95px;
    label,
    input {
      cursor: pointer;
      font-weight: normal;
    }
    input[type='checkbox'] {
      min-width: 18px;
      min-height: 18px;
    }
  }
}
</style>
